import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { AccountSection } from '../components/settings/AccountSection';
import { PrivacySection } from '../components/settings/PrivacySection';
import { NotificationSection } from '../components/settings/NotificationSection';
import { DataAccountSection } from '../components/settings/DataAccountSection';
import { LegalSection } from '../components/settings/LegalSection';
import { Skeleton } from '../components/ui/Skeleton';

const TABS = [
  { id: 'account', label: 'Account' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'data', label: 'Your Data' },
  { id: 'legal', label: 'Legal' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function isValidTab(tab: string | null): tab is TabId {
  return TABS.some((t) => t.id === tab);
}

export function SettingsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Checkout params (passed to MembershipSection via AccountSection)
  const checkoutSuccess = searchParams.get('checkout') === 'success';
  const expectedTier = searchParams.get('expected_tier') || null;
  const expectedInterval = searchParams.get('expected_interval') || null;

  const handleCheckoutHandled = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('checkout');
      next.delete('session_id');
      next.delete('expected_tier');
      next.delete('expected_interval');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Active tab from URL
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isValidTab(tabParam) ? tabParam : 'account';

  const handleTabChange = (tab: TabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: false }); // browser back/forward works
  };

  // Ref guard to ensure auth toast fires only once per mount
  const hasShownAuthToastRef = useRef(false);

  // Auth guard: redirect if not authenticated (single toast)
  useEffect(() => {
    if (!loading && !user && !hasShownAuthToastRef.current) {
      hasShownAuthToastRef.current = true;
      toast.error('Please sign in to access settings');
      navigate('/');
    }
  }, [user, loading, navigate, toast]);

  if (loading) {
    return <SettingsPageSkeleton />;
  }

  if (!user) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <div className="w-24" /> {/* Spacer for centering */}
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-hide border-b border-white/[0.06]">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === id
                  ? 'text-white border-teal-500'
                  : 'text-slate-400 border-transparent hover:text-white hover:border-white/20'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Active Tab Content — only active tab mounts */}
        {activeTab === 'account' && (
          <AccountSection
            checkoutSuccess={checkoutSuccess}
            expectedTier={expectedTier}
            expectedInterval={expectedInterval}
            onCheckoutHandled={handleCheckoutHandled}
          />
        )}

        {activeTab === 'privacy' && <PrivacySection />}

        {activeTab === 'notifications' && <NotificationSection />}

        {activeTab === 'data' && <DataAccountSection />}

        {activeTab === 'legal' && <LegalSection />}
      </div>
    </div>
  );
}

function SettingsPageSkeleton() {
  return (
    <div className="min-h-screen bg-[#111111]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-8 w-24" />
          <div className="w-24" />
        </div>
        <div className="flex gap-4 mb-6 border-b border-white/[0.06] pb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-8 w-20" />
          ))}
        </div>
        <div className="space-y-6">
          <div className="bg-[#1A1F26] rounded-xl p-6">
            <Skeleton className="h-6 w-32 mb-4" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
